import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, getDocs, query, orderBy, updateDoc, doc, where, deleteDoc } from 'firebase/firestore';
import './FacturasApp.css';

const BuscaFactura = () => {
  const [facturas, setFacturas] = useState([]);
  const [todasLasFacturas, setTodasLasFacturas] = useState([]);
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [criterioBusqueda, setCriterioBusqueda] = useState('receptorNombre');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [cargando, setCargando] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const [actualizandoTipo, setActualizandoTipo] = useState(null);
  const [eliminandoFactura, setEliminandoFactura] = useState(null);
  const [filtroAplicado, setFiltroAplicado] = useState(false);
  const [usarFiltroFecha, setUsarFiltroFecha] = useState(false);

  // Función para obtener el primer día del mes actual
  const obtenerPrimerDiaMes = () => {
    const ahora = new Date();
    return new Date(ahora.getFullYear(), ahora.getMonth(), 1).toISOString().split('T')[0];
  };

  // Función para obtener la fecha actual
  const obtenerFechaActual = () => {
    return new Date().toISOString().split('T')[0];
  };

  const cargarTodasLasFacturas = async () => {
    setCargando(true);
    try {
      const q = query(collection(db, 'EncabezadoFactura'), orderBy('fechaEmision', 'desc'));
      const querySnapshot = await getDocs(q);
      const facturasData = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        fechaEmision: doc.data().fechaEmision || ''
      }));
      
      setTodasLasFacturas(facturasData);
      
      // Aplicar filtro por defecto (mes en curso) después de cargar
      const primerDiaMes = obtenerPrimerDiaMes();
      const fechaActual = obtenerFechaActual();
      
      const facturasFiltradas = facturasData.filter(factura => {
        if (!factura.fechaEmision) return false;
        return factura.fechaEmision >= primerDiaMes && 
               factura.fechaEmision <= fechaActual + 'T23:59:59';
      });
      
      setFacturas(facturasFiltradas);
      setFiltroAplicado(false);
      
    } catch (error) {
      console.error("Error cargando todas las facturas:", error);
    }
    setCargando(false);
  };

  useEffect(() => {
    // Establecer fechas por defecto al cargar el componente
    const primerDiaMes = obtenerPrimerDiaMes();
    const fechaActual = obtenerFechaActual();
    
    setFechaDesde(primerDiaMes);
    setFechaHasta(fechaActual);
    
    cargarTodasLasFacturas();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarFacturas = async () => {
    setCargando(true);
    
    try {
      let facturasFiltradas = todasLasFacturas;

      // Aplicar filtro por fechas solo si el checkbox está activado
      if (usarFiltroFecha) {
        if (fechaDesde) {
          facturasFiltradas = facturasFiltradas.filter(factura => {
            if (!factura.fechaEmision) return false;
            return factura.fechaEmision >= fechaDesde;
          });
        }

        if (fechaHasta) {
          facturasFiltradas = facturasFiltradas.filter(factura => {
            if (!factura.fechaEmision) return false;
            return factura.fechaEmision <= fechaHasta + 'T23:59:59';
          });
        }
      }

      // Filtro por término de búsqueda
      if (terminoBusqueda.trim()) {
        facturasFiltradas = facturasFiltradas.filter(factura => {
          // Asegurarse de que el valor exista y sea una cadena antes de operar
          const valorCampo = factura[criterioBusqueda] || '';
          const valor = String(valorCampo).toLowerCase();
          
          const termino = terminoBusqueda.trim().toLowerCase();
          
          // Si se busca por tipo, usar 'includes'. Para otros, buscar coincidencia exacta o parcial.
          return valor.includes(termino);
        });
      }

      // Verificar si se aplicó algún filtro diferente al por defecto
      const hayFiltroPersonalizado = usarFiltroFecha || 
                                    terminoBusqueda.trim() !== '';
      
      setFiltroAplicado(hayFiltroPersonalizado);
      setFacturas(facturasFiltradas);
      
    } catch (error) {
      console.error("Error buscando facturas:", error);
      setFacturas(todasLasFacturas);
      setFiltroAplicado(false);
    }
    
    setCargando(false);
  };

  const handleLimpiarBusqueda = () => {
    setTerminoBusqueda('');
    
    // Restablecer a fechas por defecto
    const primerDiaMes = obtenerPrimerDiaMes();
    const fechaActual = obtenerFechaActual();
    
    setFechaDesde(primerDiaMes);
    setFechaHasta(fechaActual);
    setCriterioBusqueda('receptorNombre');
    setUsarFiltroFecha(false);
    
    // Aplicar filtro por defecto
    const facturasFiltradas = todasLasFacturas.filter(factura => {
      if (!factura.fechaEmision) return false;
      return factura.fechaEmision >= primerDiaMes && 
             factura.fechaEmision <= fechaActual + 'T23:59:59';
    });
    
    setFacturas(facturasFiltradas);
    setFiltroAplicado(false);
  };

  const verDetallesFactura = (factura) => {
    setFacturaSeleccionada(factura);
    setMostrarDetalles(true);
  };

  const cerrarDetalles = () => {
    setMostrarDetalles(false);
    setFacturaSeleccionada(null);
  };

  // Función para actualizar el tipo de factura
  const actualizarTipoFactura = async (facturaId, nuevoTipo, numeroConsecutivo) => {
    setActualizandoTipo(facturaId);
    
    try {
      const facturaActual = facturas.find(f => f.id === facturaId);
      const numConsecutivo = numeroConsecutivo || facturaActual?.numeroConsecutivo;

      if (!numConsecutivo) {
        throw new Error('No se encontró el número consecutivo de la factura');
      }

      // Actualizar EncabezadoFactura
      const encabezadoRef = doc(db, 'EncabezadoFactura', facturaId);
      await updateDoc(encabezadoRef, {
        tipoFactura: nuevoTipo
      });

      // Buscar y actualizar DetalleFactura
      try {
        const detallesQuery = query(
          collection(db, 'DetalleFactura'),
          where('numeroConsecutivo', '==', numConsecutivo)
        );
        
        const detallesSnapshot = await getDocs(detallesQuery);
        const updatePromises = [];
        
        detallesSnapshot.forEach((detalleDoc) => {
          const detalleRef = doc(db, 'DetalleFactura', detalleDoc.id);
          updatePromises.push(updateDoc(detalleRef, {
            tipoFactura: nuevoTipo
          }));
        });

        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
        }
      } catch (error) {
        console.warn('No se pudieron actualizar los detalles, pero el encabezado se actualizó:', error);
      }

      // Actualizar el estado local
      const facturasActualizadas = facturas.map(factura => 
        factura.id === facturaId 
          ? { ...factura, tipoFactura: nuevoTipo }
          : factura
      );

      const todasActualizadas = todasLasFacturas.map(factura => 
        factura.id === facturaId 
          ? { ...factura, tipoFactura: nuevoTipo }
          : factura
      );

      setFacturas(facturasActualizadas);
      setTodasLasFacturas(todasActualizadas);

    } catch (error) {
      console.error('Error al actualizar el tipo de factura:', error);
      alert('Error al actualizar el tipo de factura');
    } finally {
      setActualizandoTipo(null);
    }
  };

  // Función para eliminar una factura completa
  const eliminarFactura = async (facturaId, numeroConsecutivo) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta factura? Esta acción no se puede deshacer y eliminará tanto el encabezado como los detalles asociados.')) {
      return;
    }

    setEliminandoFactura(facturaId);
    
    try {
      // Primero eliminar los detalles de la factura
      try {
        const detallesQuery = query(
          collection(db, 'DetalleFactura'),
          where('numeroConsecutivo', '==', numeroConsecutivo)
        );
        
        const detallesSnapshot = await getDocs(detallesQuery);
        const deletePromises = [];
        
        detallesSnapshot.forEach((detalleDoc) => {
          const detalleRef = doc(db, 'DetalleFactura', detalleDoc.id);
          deletePromises.push(deleteDoc(detalleRef));
        });

        if (deletePromises.length > 0) {
          await Promise.all(deletePromises);
        }
      } catch (error) {
        console.warn('Error eliminando detalles:', error);
      }

      // Luego eliminar el encabezado
      const encabezadoRef = doc(db, 'EncabezadoFactura', facturaId);
      await deleteDoc(encabezadoRef);

      // Actualizar el estado local
      const facturasActualizadas = facturas.filter(factura => factura.id !== facturaId);
      const todasActualizadas = todasLasFacturas.filter(factura => factura.id !== facturaId);

      setFacturas(facturasActualizadas);
      setTodasLasFacturas(todasActualizadas);

      alert('Factura eliminada correctamente');

    } catch (error) {
      console.error('Error al eliminar la factura:', error);
      alert('Error al eliminar la factura');
    } finally {
      setEliminandoFactura(null);
    }
  };

  const formatearMonto = (monto) => {
    if (monto === undefined || monto === null) return '0.00';
    
    const numero = parseFloat(monto);
    if (isNaN(numero)) return '0.00';
    
    return numero.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/D';
    
    if (fecha.length === 10 && fecha.includes('-')) {
      const partes = fecha.split('-');
      if (partes.length === 3) {
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
      }
    }
    
    try {
      const date = new Date(fecha);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-ES');
      }
    } catch (error) {
      console.warn('Error formateando fecha:', error);
    }
    
    return fecha;
  };

  return (
    <div className="facturas-app">
      <header className="component-header">
        <h1>🔍 Buscador de Facturas</h1>
        <p>Busca y revisa tus facturas electrónicas</p>
        {filtroAplicado && (
          <div className="filtro-info">
            <small>Filtro personalizado aplicado</small>
          </div>
        )}
        {!filtroAplicado && (
          <div className="filtro-info">
            <small>Mostrando facturas del mes en curso</small>
          </div>
        )}
      </header>
      <div className="card search-section">
        <BuscarFacturas
          terminoBusqueda={terminoBusqueda}
          setTerminoBusqueda={setTerminoBusqueda}
          criterioBusqueda={criterioBusqueda}
          setCriterioBusqueda={setCriterioBusqueda}
          fechaDesde={fechaDesde}
          setFechaDesde={setFechaDesde}
          fechaHasta={fechaHasta}
          setFechaHasta={setFechaHasta}
          onBuscar={buscarFacturas}
          onLimpiar={handleLimpiarBusqueda}
          usarFiltroFecha={usarFiltroFecha}
          setUsarFiltroFecha={setUsarFiltroFecha}
        />
      </div>
      
      <div className="card">
        <ListaFacturas 
          facturas={facturas} 
          cargando={cargando}
          onVerDetalles={verDetallesFactura}
          onActualizarTipo={actualizarTipoFactura}
          onEliminarFactura={eliminarFactura}
          actualizandoTipo={actualizandoTipo}
          eliminandoFactura={eliminandoFactura}
          formatearMonto={formatearMonto}
          formatearFecha={formatearFecha}
        />
      </div>

      {mostrarDetalles && facturaSeleccionada && (
        <ModalDetalles 
          factura={facturaSeleccionada}
          onCerrar={cerrarDetalles}
          onEliminarFactura={eliminarFactura}
          eliminandoFactura={eliminandoFactura}
          formatearMonto={formatearMonto}
          formatearFecha={formatearFecha}
        />
      )}
    </div>
  );
};

// Componente para búsqueda
function BuscarFacturas({
  terminoBusqueda,
  setTerminoBusqueda,
  criterioBusqueda,
  setCriterioBusqueda,
  fechaDesde,
  setFechaDesde,
  fechaHasta,
  setFechaHasta,
  onBuscar,
  onLimpiar,
  usarFiltroFecha,
  setUsarFiltroFecha
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onBuscar();
  };

  return (
    <div className="buscar-facturas">
      <form onSubmit={handleSubmit} className="search-form no-title">
        <div className="search-fields">
          <div className="form-group">
            <label htmlFor="criterio">Buscar por:</label>
            <select
              id="criterio"
              value={criterioBusqueda}
              onChange={(e) => setCriterioBusqueda(e.target.value)}
            >
              <option value="receptorNombre">Nombre del Receptor</option>
              <option value="numeroConsecutivo">Número Consecutivo</option>
              <option value="tipoFactura">Tipo (Venta/Compra)</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="termino">Término de búsqueda:</label>
            <input
              id="termino"
              type="text"
              value={terminoBusqueda}
              onChange={(e) => setTerminoBusqueda(e.target.value)}
              placeholder={
                criterioBusqueda === 'fechaEmision' ? 'YYYY-MM-DD' :
                'Ingrese término a buscar'
              }
            />
          </div>

          <div className="form-group-checkbox">
            <div className="tooltip-container">
              <input 
                type="checkbox" 
                id="usarFecha" 
                checked={usarFiltroFecha} 
                onChange={(e) => setUsarFiltroFecha(e.target.checked)}
              />
              <span className="tooltip-text">Filtrar por fecha</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="fechaDesde">Fecha Desde:</label>
            <input
              id="fechaDesde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              disabled={!usarFiltroFecha}
            />
          </div>

          <div className="form-group">
            <label htmlFor="fechaHasta">Fecha Hasta:</label>
            <input
              id="fechaHasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              disabled={!usarFiltroFecha}
            />
          </div>
        </div>
        
        <div className="search-actions">
          <button type="submit" className="btn btn-primary">
            Buscar
          </button>
          <button type="button" onClick={onLimpiar} className="btn btn-secondary">
            Limpiar
          </button>
        </div>
      </form>
    </div>
  );
}

// Los componentes ListaFacturas y ModalDetalles se mantienen igual...

function ListaFacturas({ 
  facturas, 
  cargando, 
  onVerDetalles, 
  onActualizarTipo, 
  onEliminarFactura,
  actualizandoTipo, 
  eliminandoFactura,
  formatearMonto, 
  formatearFecha
}) {
  if (cargando) {
    return (
      <>
        <h2>📄 Facturas Almacenadas</h2>
        <div className="loading">Cargando facturas...</div>
      </>
    );
  }

  const esFacturaCompra = (tipoFactura) => {
    if (!tipoFactura) return false;
    const tipo = tipoFactura.toLowerCase();
    return tipo.includes('compra') || tipo.includes('gasto');
  };

  const obtenerOpcionesCombobox = (tipoActual) => {
    const opciones = [
      { valor: 'Gastos', etiqueta: 'Gastos' },
      { valor: 'NC Gastos', etiqueta: 'NC Gastos' },
      { valor: 'Compras', etiqueta: 'Compras' }
    ];

    if (tipoActual && !opciones.some(op => op.valor === tipoActual)) {
      opciones.unshift({ valor: tipoActual, etiqueta: tipoActual });
    }

    return opciones;
  };

  const handleTipoChange = (facturaId, currentType, nuevoTipo, numeroConsecutivo) => {
    if (currentType === nuevoTipo) return;
    
    if (window.confirm('¿Está seguro de que desea cambiar el tipo de factura? Esta acción actualizará tanto el encabezado como los detalles.')) {
      onActualizarTipo(facturaId, nuevoTipo, numeroConsecutivo);
    }
  };

  const handleEliminar = (facturaId, numeroConsecutivo) => {
    onEliminarFactura(facturaId, numeroConsecutivo);
  };

  return (
    <div className="lista-facturas">
      <h2 className="section-title">📄 Facturas Almacenadas ({facturas.length})</h2>
      
      {facturas.length === 0 ? (
        <div className="empty-state">
          <p>No se encontraron facturas.</p>
        </div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="facturas-table">
              <thead>
                <tr>
                  <th>Número Consecutivo</th>
                  <th>Tipo</th>
                  <th>Emisor</th>
                  <th>Receptor</th>
                  <th>Emisión</th>
                  <th>Total</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map(factura => {
                  const esCompra = esFacturaCompra(factura.tipoFactura);
                  const opciones = obtenerOpcionesCombobox(factura.tipoFactura);
                  
                  return (
                    <tr key={factura.id}>
                      <td>{factura.numeroConsecutivo}</td>
                      <td>
                        {esCompra ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <select
                              value={factura.tipoFactura || ''}
                              onChange={(e) => handleTipoChange(factura.id, factura.tipoFactura, e.target.value, factura.numeroConsecutivo)}
                              disabled={actualizandoTipo === factura.id}
                              className="tipo-select"
                            >
                              {opciones.map((opcion, index) => (
                                <option key={`${opcion.valor}-${index}`} value={opcion.valor}>
                                  {opcion.etiqueta}
                                </option>
                              ))}
                            </select>
                            {actualizandoTipo === factura.id && (
                              <span className="loading-small">Actualizando...</span>
                            )}
                          </div>
                        ) : (
                          <span className={`tipo-badge ${(factura.tipoFactura || '').toLowerCase().replace(/\s+/g, '-')}`}>
                            {factura.tipoFactura}
                          </span>
                        )}
                      </td>
                      <td>{factura.emisorNombre}</td>
                      <td>{factura.receptorNombre}</td>
                      <td>{formatearFecha(factura.fechaEmision)}</td>
                      <td className="monetary">{formatearMonto(factura.totalComprobante)} {factura.codigoMoneda || 'CRC'}</td>
                      <td>
                        <div className="actions-cell">
                          <div className="tooltip-container">
                            <button className="icon-btn" onClick={() => onVerDetalles(factura)}>
                              ↗️
                            </button>
                            <span className="tooltip-text">Ver Detalles</span>
                          </div>
                          <div className="tooltip-container">
                            <button className="icon-btn danger" onClick={() => handleEliminar(factura.id, factura.numeroConsecutivo)} disabled={eliminandoFactura === factura.id}>
                              {eliminandoFactura === factura.id ? '...' : '🗑️'}
                            </button>
                            <span className="tooltip-text">Eliminar Factura</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente Modal para detalles de factura (se mantiene igual)
function ModalDetalles({ factura, onCerrar, onEliminarFactura, eliminandoFactura, formatearMonto, formatearFecha }) {
  
  const handleEliminarDesdeModal = () => {
    if (window.confirm('¿Está seguro de que desea eliminar esta factura? Esta acción no se puede deshacer.')) {
      onEliminarFactura(factura.id, factura.numeroConsecutivo);
      onCerrar();
    }
  };

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Detalles de Factura: {factura.numeroConsecutivo}</h2>
          <button className="modal-close" onClick={onCerrar}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="detalles-grid">
            <div className="detalles-col">
              <h3>Información General</h3>
              <p><strong>Clave:</strong> {factura.clave}</p>
              <p><strong>Fecha Emisión:</strong> {formatearFecha(factura.fechaEmision)}</p>
              <p><strong>Tipo:</strong> {factura.tipoFactura}</p>
              <p><strong>Moneda:</strong> {factura.codigoMoneda || 'CRC'}</p>
              <p><strong>Tipo Cambio:</strong> <span className="monetary">{formatearMonto(factura.tipoCambio)}</span></p>
            </div>
            
            <div className="detalles-col">
              <h3>Datos del Emisor</h3>
              <p><strong>Nombre:</strong> {factura.emisorNombre}</p>
              <p><strong>Identificación:</strong> {factura.emisorTipoIdentificacion} {factura.emisorNumeroIdentificacion}</p>
              <p><strong>Teléfono:</strong> {factura.emisorTelefonoCodigoPais} {factura.emisorTelefonoNumero}</p>
              <p><strong>Email:</strong> {factura.emisorCorreoElectronico}</p>
            </div>
            
            <div className="detalles-col">
              <h3>Datos del Receptor</h3>
              <p><strong>Nombre:</strong> {factura.receptorNombre}</p>
              <p><strong>Identificación:</strong> {factura.receptorTipoIdentificacion} {factura.receptorNumeroIdentificacion}</p>
            </div>
            
            <div className="detalles-col">
              <h3>Totales</h3>
              <p><strong>Total Gravado:</strong> <span className="monetary">{formatearMonto(factura.totalGravado)}</span></p>
              <p><strong>Total Exento:</strong> <span className="monetary">{formatearMonto(factura.totalExento)}</span></p>
              <p><strong>Total Exonerado:</strong> <span className="monetary">{formatearMonto(factura.totalExonerado)}</span></p>
              <p><strong>Total Descuentos:</strong> <span className="monetary">{formatearMonto(factura.totalDescuentos)}</span></p>
              <p><strong>Total Impuesto:</strong> <span className="monetary">{formatearMonto(factura.totalImpuesto)}</span></p>
              <p><strong>Total Comprobante:</strong> <span className="monetary">{formatearMonto(factura.totalComprobante)}</span> {factura.codigoMoneda || 'CRC'}</p>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="btn btn-danger"
            onClick={handleEliminarDesdeModal}
            disabled={eliminandoFactura === factura.id}
          >
            {eliminandoFactura === factura.id ? 'Eliminando...' : 'Eliminar Factura'}
          </button>
          <button className="btn btn-primary" onClick={onCerrar}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default BuscaFactura;